import { useEffect, useState } from 'react';
import {
  PROJECT_PRIORITY_COLORS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_PRIORITY_ORDER,
} from '../../pm/constants';
import { dateInputToMillis } from '../../pm/dueDate';
import {
  ProjectPriority,
  ProjectTemplate,
  TemplateTask,
  TemplateWorkstream,
} from '../../pm/types';

interface Props {
  open: boolean;
  template: ProjectTemplate | null;
  // Counts shown in the modal so users know what they're about to create.
  workstreams: TemplateWorkstream[];
  tasks: TemplateTask[];
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (input: {
    title: string;
    description: string;
    priority?: ProjectPriority;
    startDate?: number;
  }) => void;
}

const UseTemplateModal: React.FC<Props> = ({
  open,
  template,
  workstreams,
  tasks,
  busy,
  error,
  onCancel,
  onSubmit,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<ProjectPriority | ''>('');
  const [startDate, setStartDate] = useState('');

  // Reset draft state whenever the modal is (re)opened against a template.
  // Pre-fill the title with the template name so the common case is one click.
  useEffect(() => {
    if (open && template) {
      setTitle(template.title);
      setDescription(template.description || '');
      setPriority('');
      setStartDate('');
    }
  }, [open, template]);

  if (!open || !template) return null;

  const wsCount = workstreams.filter((w) => w.templateId === template.id).length;
  const taskCount = tasks.filter((t) => t.templateId === template.id).length;
  const hasOffsets = tasks.some(
    (t) => t.templateId === template.id && typeof t.dueOffsetDays === 'number',
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const ms = startDate ? dateInputToMillis(startDate) : null;
    onSubmit({
      title: title.trim(),
      description: description.trim(),
      ...(priority ? { priority } : {}),
      ...(typeof ms === 'number' ? { startDate: ms } : {}),
    });
  }

  return (
    <div
      className="pm-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Use template"
      onClick={onCancel}
    >
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <header className="pm-modal-header">
          <h2>Use template</h2>
          <button
            type="button"
            className="pm-link-button"
            onClick={onCancel}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="pm-modal-summary">
          From <strong>{template.title}</strong> · {wsCount} workstream{wsCount === 1 ? '' : 's'} · {taskCount} task{taskCount === 1 ? '' : 's'}
          {hasOffsets ? ' · some tasks have date offsets' : ''}
        </div>

        {error ? <div className="pm-error">{error}</div> : null}

        <form className="pm-modal-form" onSubmit={handleSubmit}>
          <label className="pm-modal-field">
            <span className="pm-modal-field-label">Project title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
            />
          </label>

          <label className="pm-modal-field">
            <span className="pm-modal-field-label">Description (optional)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </label>

          <div className="pm-modal-row">
            <label className="pm-modal-field">
              <span className="pm-modal-field-label">Priority</span>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as ProjectPriority | '')}
              >
                <option value="">Unprioritized</option>
                {PROJECT_PRIORITY_ORDER.map((p) => (
                  <option key={p} value={p}>{PROJECT_PRIORITY_LABELS[p]}</option>
                ))}
              </select>
            </label>

            <label className="pm-modal-field">
              <span className="pm-modal-field-label">
                Start date {hasOffsets ? '(used for task due dates)' : '(optional)'}
              </span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
          </div>

          {priority ? (
            <div className="pm-modal-priority-preview">
              <span
                className="pm-priority-dot"
                style={{ background: PROJECT_PRIORITY_COLORS[priority] }}
              />
              <span>{PROJECT_PRIORITY_LABELS[priority]}</span>
            </div>
          ) : null}

          <div className="pm-modal-actions">
            <button type="button" className="pm-button" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className="pm-button pm-button-primary"
              disabled={busy || !title.trim()}
            >
              {busy ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UseTemplateModal;
